import type { RadiusLevel } from './settings'
import type { ThemeMode } from './tokens/semantic'
import type { SurfaceTint } from './tokens/tint'

export type ThemePreset = {
  key: string
  /** Tên hiển thị (tiếng Anh ngắn gọn). */
  label: string
  /** Emoji đại diện cho preset — hiển thị trong card. */
  emoji: string
  /** Màu chủ đạo hex, chỉ khai báo ở đây — không gõ lại trong .tsx. */
  colorPrimary: string
  /** Màu bổ trợ tuỳ chọn (colorSuccess, accent). */
  colorSecondary?: string
  /** Chế độ sáng/tối preset được thiết kế cho (mặc định sáng). */
  mode?: ThemeMode
  /** Bộ nền riêng (trang, sider/header/thẻ, vùng lõm, popover) cho `mode` của preset. */
  surfaces: Pick<SurfaceTint, 'page' | 'surface' | 'sunken'>
  /** Mức bo góc đóng gói sẵn trong preset. */
  radiusLevel: RadiusLevel
}

/**
 * Danh sách theme preset tên gọi — giống kiểu DaisyUI.
 * Hex chỉ được khai báo TẠI ĐÂY; .tsx lấy qua object này, không hardcode.
 */
export const THEME_PRESETS: ThemePreset[] = [
  {
    key: 'ocean',
    label: 'Ocean',
    emoji: '🌊',
    colorPrimary: '#1668dc',
    surfaces: { page: '#E6F0FD', surface: '#F6F9FF', sunken: '#D6E5FA' },
    radiusLevel: 'lg',
  },
  {
    key: 'forest',
    label: 'Forest',
    emoji: '🌿',
    colorPrimary: '#2e7d32',
    colorSecondary: '#00786f',
    surfaces: { page: '#E4F1E6', surface: '#F5FAF5', sunken: '#D3E8D6' },
    radiusLevel: 'md',
  },
  {
    key: 'luxury',
    label: 'Luxury',
    emoji: '💜',
    colorPrimary: '#722ed1',
    colorSecondary: '#c41d7f',
    surfaces: { page: '#EFE8FA', surface: '#FAF7FE', sunken: '#E2D6F4' },
    radiusLevel: 'sm',
  },
  {
    key: 'sunset',
    label: 'Sunset',
    emoji: '🌅',
    colorPrimary: '#c2410c',
    colorSecondary: '#dc2626',
    surfaces: { page: '#FBEADB', surface: '#FFF8F0', sunken: '#F5D8C0' },
    radiusLevel: 'lg',
  },
  {
    key: 'peach',
    label: 'Peach',
    emoji: '🍑',
    colorPrimary: '#d4380d',
    surfaces: { page: '#FCE7E0', surface: '#FFF6F3', sunken: '#F7D3C8' },
    radiusLevel: 'lg',
  },
  {
    key: 'mint',
    label: 'Mint',
    emoji: '🪴',
    colorPrimary: '#0e7490',
    surfaces: { page: '#DFF2F4', surface: '#F2FBFC', sunken: '#CBE8EC' },
    radiusLevel: 'lg',
  },
  {
    key: 'sakura',
    label: 'Sakura',
    emoji: '🌸',
    colorPrimary: '#c41d7f',
    surfaces: { page: '#FBE6F0', surface: '#FFF5F9', sunken: '#F4D3E4' },
    radiusLevel: 'lg',
  },
  {
    key: 'midnight',
    label: 'Midnight',
    emoji: '🌑',
    colorPrimary: '#2f4bd8',
    mode: 'dark',
    surfaces: { page: '#0B1020', surface: '#131A2E', sunken: '#1B2542' },
    radiusLevel: 'sm',
  },
  {
    key: 'autumn',
    label: 'Autumn',
    emoji: '🍂',
    colorPrimary: '#ad4e00',
    colorSecondary: '#d48806',
    surfaces: { page: '#F1EBE2', surface: '#FBF8F3', sunken: '#E5DBCE' },
    radiusLevel: 'md',
  },
  {
    key: 'classic',
    label: 'Classic',
    emoji: '🏛',
    colorPrimary: '#1f6cb0',
    surfaces: { page: '#EDF0F4', surface: '#FFFFFF', sunken: '#E1E6ED' },
    radiusLevel: 'md',
  },
]

/**
 * Áp dụng một preset: ghi customPrimary, radiusLevel và activePreset vào LayoutSettings.
 * setSetting được truyền vào từ LayoutSettingDrawer — không couple vào context ở đây.
 */
export function applyPreset(
  preset: ThemePreset,
  setSetting: <K extends string>(key: K, value: unknown) => void
): void {
  setSetting('customPrimary', preset.colorPrimary)
  setSetting('radiusLevel', preset.radiusLevel)
  setSetting('activePreset', preset.key)
}

/**
 * Bộ nền của preset đang active cho chế độ `mode`; undefined = preset không thiết kế
 * cho chế độ này (hoặc không có preset) → dùng nền sinh tự động từ màu chủ đạo.
 */
export const presetSurfaces = (
  key: string | undefined,
  mode: ThemeMode
): ThemeSurfaces | undefined => {
  const preset = THEME_PRESETS.find((p) => p.key === key)
  return preset && (preset.mode ?? 'light') === mode ? preset.surfaces : undefined
}

type ThemeSurfaces = ThemePreset['surfaces']
